// external
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// interstate
import { Box } from '@interstate/components/Box';
import { CheckBox } from '@interstate/components/CheckBox';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import type { TextInputEventValue } from '@interstate/components/TextInput';
import { Typography } from '@interstate/components/Typography';

// utils
import { kebabCase } from '@makemydeal/dr-common-utils';
import {
    createTrueKeysObject,
    getHumanReadableTaxName,
    getTotalTaxRate,
    hasTaxBreakdown,
    isTaxRateKey,
    objectHasNumericValue
} from './helpers';

// custom components
import { dealLockHooks, PaymentLoading } from '@makemydeal/dr-dash-components';
import { LoadingSectionParent } from '@makemydeal/dr-shared-components';
import MonthlyTaxSummary from './MonthlyTaxSummary';
import TaxItemSummaryDetailed from './TaxItemSummaryDetailed';
import TaxItemTotalField from './TaxItemTotalField';

// redux store and selectors
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as deskingSelectors from '@makemydeal/dr-dash-store/selectors/deskingSelectors';
import * as calculationDetailsSelectors from '@makemydeal/dr-dash-store/selectors/calculationDetailsSelectors';
import * as deskingActionCreators from '@makemydeal/dr-dash-store/actions/deskingActionCreators';

import { actionCreators } from '@makemydeal/dr-offer-redux';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

// types
import type { paymentServicesTypes } from '@makemydeal/dr-platform-types';
import { TaxClasses, TaxNames } from '@makemydeal/dr-platform-types';

// constants and helpers
import { TAX_AMOUNT_TOTAL_THRESHOLD, taxNameMappingTable, TAXRATE_TOTAL_THRESHOLD } from '../constants';

// CSS styles
import { StyledPriceInputBaseContainer, StyledTaxesContainer, StyledTaxName } from './TaxItemSummary.style';
import TaxTotalRateField from './TaxTotalRateField';
interface TaxItemSummaryProps {
    taxItem: paymentServicesTypes.TaxItem;
    taxOverrideEnabled?: boolean;
    hideTaxName?: boolean;
    taxIndex?: number;
    disabledCrtUpfrontCheckbox?: boolean;
    isTaxesFormDirtyRef?: React.MutableRefObject<boolean>;
}

const TaxItemSummary: React.FC<TaxItemSummaryProps> = ({
    taxItem,
    disabledCrtUpfrontCheckbox,
    taxOverrideEnabled,
    hideTaxName,
    taxIndex,
    isTaxesFormDirtyRef
}) => {
    const theme = useInterstateTheme();
    const dispatch = useDispatch();

    const offerType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const showTotalTaxRateSupersedeAlert = useSelector(deskingSelectors.shouldShowTotalTaxRateSupersedeAlert);
    const isTotalTaxRateFieldEnabled = useSelector(featureToggleSelectors.isTotalTaxRateFieldEnabled);
    const taxItemsWithOverridesWithoutFlat = useSelector(offerReduxSelectors.getTaxItemsWithOverridesWithoutFlat);
    const isCapReductionTaxCapitalizedIndicator = useSelector(offerReduxSelectors.getCapReductionTaxCapitalizedIndicator);
    const initCapReductionTaxCapitalizedIndicator = useSelector(calculationDetailsSelectors.isCapReductionTaxCapitalized);

    const [isCrtUpfront, setIsCrtUpfront] = useState(
        isCapReductionTaxCapitalizedIndicator || initCapReductionTaxCapitalizedIndicator
    );
    const [showDetailedTaxSummary, setShowDetailedTaxSummary] = useState(false);
    const [isTotalTaxRateOverThreshold, setIsTotalTaxRateOverThreshold] = useState(false);
    const [taxBreakdownRateLocal, setTaxBreakdownRateLocal] = useState({});

    const isLocked = dealLockHooks.useIsDealLocked();

    const taxItemBreakDownHasValues = hasTaxBreakdown(taxItem) && objectHasNumericValue(taxItem.taxBreakdown);
    const shouldShowSummaryDetail = taxItemBreakDownHasValues || showDetailedTaxSummary;
    const isMonthlyTaxItem = taxItem.monthlyTax != null;

    useEffect(() => {
        setIsCrtUpfront(isCapReductionTaxCapitalizedIndicator);
    }, [isCapReductionTaxCapitalizedIndicator]); // isCapReductionTaxCapitalizedIndicator is intentionally left out

    useEffect(() => {
        if (!showTotalTaxRateSupersedeAlert) {
            setTaxBreakdownRateLocal({});
            setIsTotalTaxRateOverThreshold(false);
        }
    }, [showTotalTaxRateSupersedeAlert]);

    // Track dirty state for unsaved changes modal
    useEffect(() => {
        if (isTaxesFormDirtyRef) {
            isTaxesFormDirtyRef.current = isTotalTaxRateOverThreshold;
        }
    }, [isTotalTaxRateOverThreshold]);

    const handleCrtUpfrontChange = useCallback(
        (isUpfront: boolean) => {
            if (taxItemsWithOverridesWithoutFlat?.length) {
                const updatedItems = taxItemsWithOverridesWithoutFlat.map((taxItem) =>
                    taxItem.taxName === TaxNames.CapCostReduction
                        ? { ...taxItem, isCapitalize: !isUpfront, isManualTax: true }
                        : taxItem
                );

                dispatch(actionCreators.updateTaxItems(updatedItems, offerType, true));
            }
        },
        [dispatch, offerType, taxItemsWithOverridesWithoutFlat]
    );

    const handleTaxBreakdownChange =
        (breakdownType: 'amount' | 'rate') => (event: InterstateOnChangeEvent<TextInputEventValue>, keyName: string) => {
            const newRate = parseFloat(event.target.value) || 0;
            const taxBreakdown = taxItem.taxBreakdown as Record<string, any>;

            if (isTaxRateKey(keyName)) {
                const local = {
                    ...taxBreakdownRateLocal,
                    [keyName]: newRate
                };

                const totalTaxRate = getTotalTaxRate(taxBreakdown, local);

                if (totalTaxRate >= TAXRATE_TOTAL_THRESHOLD) {
                    setTaxBreakdownRateLocal(local);
                    setIsTotalTaxRateOverThreshold(true);
                    dispatch(deskingActionCreators.showTotalTaxRateSupersedeAlert());
                    return;
                }

                dispatch(deskingActionCreators.hideTotalTaxRateSupersedeAlert());
            }

            if (taxBreakdown?.[keyName] === newRate || (!taxBreakdown && taxItem.taxRate === newRate)) {
                return;
            }

            const isAmountExceedThreshold =
                taxItem.taxBreakdown &&
                Object.values({ ...taxItem.taxBreakdown, [keyName]: newRate }).some(
                    (value) => typeof value === 'number' && value > TAX_AMOUNT_TOTAL_THRESHOLD
                );

            const updatedTaxItem = {
                ...taxItem,
                taxBreakdown: {
                    ...taxItem.taxBreakdown,
                    ...taxBreakdownRateLocal,
                    [keyName]: newRate
                },
                manualChanges: {
                    ...taxItem.manualChanges,
                    ...createTrueKeysObject(Object.keys(taxBreakdownRateLocal)),
                    [keyName]: true,
                    ...(breakdownType === 'amount' && {
                        cityTaxRate: false,
                        countyTaxRate: false,
                        stateTaxRate: false,
                        localTaxRate: false
                    })
                },
                isManualTax: true,
                ...(breakdownType === 'amount' && { isManualTaxRate: false, isManualTaxAmount: true }),
                ...(breakdownType === 'rate' && { isManualTaxRate: true, isManualTaxAmount: false })
            };

            if (isAmountExceedThreshold) {
                return;
            }
            dispatch(actionCreators.updateTaxItems([updatedTaxItem], offerType, true));
            dispatch(deskingActionCreators.hideTotalTaxRateSupersedeAlert());
        };

    const handleTaxTotalItemChange = (event: InterstateOnChangeEvent<TextInputEventValue>) => {
        const fieldName = isMonthlyTaxItem ? 'monthlyTax' : 'taxAmount';
        const newValue = !isNaN(parseFloat(event.target.value)) ? parseFloat(event.target.value) : 0;

        if (taxItem[fieldName] === newValue) {
            return;
        }

        /**
         * Under feature flag, display "--" for rate and amount,
         * when one of the total tax item is overridden and the tax item has breakdowns.
         * That way, it will be visible that breakdown items were overridden because the total was overridden.
         */
        if (objectHasNumericValue(taxItem.taxBreakdown)) {
            setShowDetailedTaxSummary(true);
        }

        const emptyBreakdown = {
            taxBreakdown: undefined
        };

        const updatedTaxItem = {
            ...taxItem,
            manualChanges: {
                taxAmount: true
            },
            [fieldName]: newValue,
            isManualTax: true,
            isManualTaxRate: false,
            isManualTaxAmount: true,
            ...(isMonthlyTaxItem &&
                // istanbul ignore next
                emptyBreakdown)
        };

        const isAmountExceedThreshold = newValue > TAX_AMOUNT_TOTAL_THRESHOLD;

        if (isAmountExceedThreshold) {
            return;
        }
        dispatch(actionCreators.updateTaxItems([updatedTaxItem], offerType, true));
        dispatch(deskingActionCreators.hideTotalTaxRateSupersedeAlert());
    };

    const renderTaxName = () => {
        // Property tax (additional-class monthly tax) shows the backend-provided description (e.g. "Mo. Property
        // Tax"); every other row falls back to the human-readable tax-name mapping.
        const displayTaxName =
            isMonthlyTaxItem && taxItem.class === TaxClasses.ADDITIONAL && taxItem.taxDescription
                ? taxItem.taxDescription
                : getHumanReadableTaxName(taxItem.taxName, taxNameMappingTable);
        return hideTaxName ? (
            // this div is here by intention to layout correctly when the total of monthly tax is overriden and tab visited again.
            <div></div>
        ) : (
            <StyledTaxName>
                <Typography variant="body-md">{displayTaxName}</Typography>
                {taxItem.taxName === TaxNames.CapCostReduction && !taxOverrideEnabled && (
                    <CheckBox
                        sx={{
                            backgroundColor: 'inherit'
                        }}
                        data-testid="cap-cost-reduction-upfront-checkbox"
                        id={kebabCase('cap-cost-reduction-upfront')}
                        label="Upfront"
                        disabled={disabledCrtUpfrontCheckbox || isLocked}
                        name={kebabCase('cap-cost-reduction-upfront')}
                        onChange={(event) => handleCrtUpfrontChange(event.target.value.checked)}
                        checked={!isCrtUpfront}
                        value="1"
                    />
                )}
            </StyledTaxName>
        );
    };

    const renderTaxSummary = () => {
        return (
            <TaxItemSummaryDetailed
                taxItem={taxItem}
                key={taxItem.taxName}
                handleChange={handleTaxBreakdownChange}
                taxOverrideEnabled={taxOverrideEnabled}
                isTotalTaxRateOverThreshold={isTotalTaxRateOverThreshold}
            />
        );
    };

    const renderTaxAmount = () => {
        return (
            <Box display="flex" justifyContent="flex-end">
                {isTotalTaxRateFieldEnabled && (
                    <StyledPriceInputBaseContainer>
                        <TaxTotalRateField taxItem={taxItem} taxOverrideEnabled={taxOverrideEnabled} offerType={offerType} />
                    </StyledPriceInputBaseContainer>
                )}
                <StyledPriceInputBaseContainer>
                    <TaxItemTotalField
                        key={taxItem.taxAmount}
                        data={taxItem}
                        handleBlur={handleTaxTotalItemChange}
                        shouldDisableInput={taxOverrideEnabled}
                    />
                </StyledPriceInputBaseContainer>
            </Box>
        );
    };

    const renderMonthlyTax = () => {
        return (
            <Box display="flex" justifyContent="flex-end">
                <MonthlyTaxSummary
                    taxItem={taxItem}
                    offerType={offerType}
                    taxOverrideEnabled={taxOverrideEnabled}
                    handleAmountBlur={handleTaxTotalItemChange}
                />
            </Box>
        );
    };

    return (
        <LoadingSectionParent>
            <PaymentLoading testId="tax-items-loading-indicator" />
            <StyledTaxesContainer removeBorder={!taxIndex} data-testid={`individualTax-${taxItem.taxName}`} theme={theme}>
                {isMonthlyTaxItem ? (
                    <>
                        {renderTaxName()}
                        {renderMonthlyTax()}
                    </>
                ) : shouldShowSummaryDetail || taxOverrideEnabled ? (
                    <>
                        {renderTaxName()}
                        {renderTaxSummary()}
                        {renderTaxAmount()}
                    </>
                ) : (
                    <>
                        {renderTaxName()}
                        {renderTaxAmount()}
                    </>
                )}
            </StyledTaxesContainer>
        </LoadingSectionParent>
    );
};

export default TaxItemSummary;
